// Element type catalog — single source of truth for the floor plan editor
// Colors, default dimensions, shapes, and toolbar display info

export const GRID_SIZE = 20;
export const CANVAS_WIDTH = 1200;
export const CANVAS_HEIGHT = 800;

export const ELEMENT_COLORS = {
  table: '#4CAF50',
  booth: '#66BB6A',
  'bar-stool': '#81C784',
  'high-top': '#A5D6A7',
  'high-table': '#A5D6A7',
  counter: '#C8E6C9',
  door: '#8D6E63',
  window: '#90CAF9',
  ac_vent: '#42A5F5',
  fan: '#64B5F6',
  counter_bar: '#BDBDBD',
  kitchen: '#9E9E9E',
  restroom: '#CE93D8',
  disabled_bathroom: '#AB47BC',
  disabled_parking: '#7E57C2',
  stairs: '#FF8A65',
  ramp: '#4DB6AC',
  raised_section: '#FFCC80',
  speaker: '#EF5350',
  stage: '#E57373',
  wall: '#616161',
  inside_zone: 'rgba(200, 230, 255, 0.3)',
  outside_zone: 'rgba(200, 255, 200, 0.3)',
  raised_section_zone: 'rgba(255, 200, 128, 0.3)',
  quiet_zone: 'rgba(200, 200, 255, 0.3)',
  smoking_zone: 'rgba(255, 200, 200, 0.3)',
};

export const TABLE_TYPES = [
  { subType: 'standard-2', label: 'Standard (2-seat)', shape: 'square', width: 60, height: 60, seatCount: 2, tableType: 'standard' },
  { subType: 'standard-4', label: 'Standard (4-seat)', shape: 'square', width: 80, height: 80, seatCount: 4, tableType: 'standard' },
  { subType: 'standard-6', label: 'Standard (6-seat)', shape: 'rectangle', width: 120, height: 80, seatCount: 6, tableType: 'standard' },
  { subType: 'standard-8', label: 'Standard (8-seat)', shape: 'rectangle', width: 160, height: 80, seatCount: 8, tableType: 'standard' },
  { subType: 'round-4', label: 'Round (4-seat)', shape: 'circle', width: 80, height: 80, seatCount: 4, tableType: 'standard' },
  { subType: 'round-6', label: 'Round (6-seat)', shape: 'circle', width: 100, height: 100, seatCount: 6, tableType: 'standard' },
  { subType: 'booth-2', label: 'Booth (2-seat)', shape: 'rounded-rect', width: 80, height: 60, seatCount: 2, tableType: 'booth' },
  { subType: 'booth-4', label: 'Booth (4-seat)', shape: 'rounded-rect', width: 80, height: 100, seatCount: 4, tableType: 'booth' },
  { subType: 'booth-6', label: 'Booth (6-seat)', shape: 'rounded-rect', width: 80, height: 140, seatCount: 6, tableType: 'booth' },
  { subType: 'bar-stool', label: 'Bar Stool', shape: 'circle', width: 40, height: 40, seatCount: 1, tableType: 'bar-stool' },
  { subType: 'high-top-2', label: 'High-Top (2-seat)', shape: 'square', width: 50, height: 50, seatCount: 2, tableType: 'high-top' },
  { subType: 'high-table-2', label: 'High-Table (2-seat)', shape: 'square', width: 50, height: 50, seatCount: 2, tableType: 'high-table' },
  { subType: 'high-table-4', label: 'High-Table (4-seat)', shape: 'square', width: 70, height: 70, seatCount: 4, tableType: 'high-table' },
  { subType: 'counter-seat', label: 'Counter Seat', shape: 'square', width: 40, height: 40, seatCount: 1, tableType: 'counter' },
];

export const FIXED_ELEMENT_TYPES = [
  { subType: 'door', label: 'Door', icon: 'MeetingRoom', width: 60, height: 10 },
  { subType: 'window', label: 'Window', icon: 'Window', width: 80, height: 10 },
  { subType: 'ac_vent', label: 'AC Vent', icon: 'AcUnit', width: 40, height: 40 },
  { subType: 'fan', label: 'Fan', icon: 'Air', width: 40, height: 40 },
  { subType: 'counter_bar', label: 'Counter/Bar', icon: 'Countertops', width: 200, height: 40 },
  { subType: 'kitchen', label: 'Kitchen', icon: 'Restaurant', width: 150, height: 100 },
  { subType: 'restroom', label: 'Restroom', icon: 'Wc', width: 80, height: 80 },
  { subType: 'disabled_bathroom', label: 'Disabled Bathroom', icon: 'Accessible', width: 100, height: 100 },
  { subType: 'disabled_parking', label: 'Disabled Parking', icon: 'LocalParking', width: 80, height: 80 },
  { subType: 'stairs', label: 'Stairs', icon: 'Stairs', width: 60, height: 100 },
  { subType: 'ramp', label: 'Ramp', icon: 'Accessibility', width: 60, height: 100 },
  { subType: 'raised_section', label: 'Raised Section', icon: 'Layers', width: 200, height: 150 },
  { subType: 'speaker', label: 'Speaker', icon: 'VolumeUp', width: 30, height: 30 },
  { subType: 'stage', label: 'Stage/Music', icon: 'MusicNote', width: 150, height: 100 },
  { subType: 'wall', label: 'Wall', icon: 'HorizontalRule', width: 200, height: 10 },
];

export const ZONE_TYPES = [
  { subType: 'inside_zone', label: 'Inside Zone', width: 300, height: 300 },
  { subType: 'outside_zone', label: 'Outside Zone', width: 300, height: 300 },
  { subType: 'raised_section_zone', label: 'Raised Section', width: 200, height: 200 },
  { subType: 'quiet_zone', label: 'Quiet Zone', width: 200, height: 200 },
  { subType: 'smoking_zone', label: 'Smoking Area', width: 150, height: 150 },
];

// Create a new element with defaults
export function createDefaultElement(category, subType, x, y, nextTableNo) {
  const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const base = { id, x, y, rotation: 0, label: '' };

  if (category === 'table') {
    const def = TABLE_TYPES.find((t) => t.subType === subType);
    if (!def) return null;
    return {
      ...base,
      type: 'table',
      subType: def.subType,
      width: def.width,
      height: def.height,
      shape: def.shape,
      tableNo: nextTableNo,
      seatCount: def.seatCount,
      tableType: def.tableType,
      wheelchairAccessible: def.tableType !== 'booth' && def.tableType !== 'bar-stool' && def.tableType !== 'high-top' && def.tableType !== 'high-table',
      elevated: false,
      label: `T${nextTableNo}`,
      zone: 'inside',
    };
  }

  if (category === 'fixed') {
    const def = FIXED_ELEMENT_TYPES.find((t) => t.subType === subType);
    if (!def) return null;
    const el = {
      ...base,
      type: 'fixed',
      subType: def.subType,
      width: def.width,
      height: def.height,
      label: def.label,
      zone: 'inside',
    };
    // Door defaults
    if (subType === 'door') {
      el.openMechanism = 'push';
      el.openDirection = 'inward';
      el.automatic = false;
      el.doorWeight = 'standard';
      el.doorType = 'main';
      el.doorWidth = 'standard';
    }
    // Fan defaults
    if (subType === 'fan') {
      el.fanType = 'ceiling';
    }
    // Restroom defaults
    if (subType === 'restroom') {
      el.accessible = false;
      el.bathroomSize = 'standard';
      el.ownership = 'private';
    }
    // Disabled bathroom defaults
    if (subType === 'disabled_bathroom') {
      el.accessible = true;
      el.bathroomSize = 'large';
      el.ownership = 'private';
      el.features = [];
    }
    // Disabled parking defaults
    if (subType === 'disabled_parking') {
      el.spotCount = 1;
      el.distanceFromEntry = '';
      el.parkingOwnership = 'venue';
    }
    return el;
  }

  if (category === 'zone') {
    const def = ZONE_TYPES.find((t) => t.subType === subType);
    if (!def) return null;
    return {
      ...base,
      type: 'zone',
      subType: def.subType,
      width: def.width,
      height: def.height,
      label: def.label,
    };
  }

  return null;
}

// Snap value to grid
export function snapToGrid(value) {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}
